import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const remodelingTypes = [
  {
    icon: 'ri-home-gear-line',
    title: 'Kitchen Remodeling',
    description: 'Transform your kitchen with custom cabinetry, countertops, appliances, and layouts designed for modern living.'
  },
  {
    icon: 'ri-drop-line',
    title: 'Bathroom Remodeling',
    description: 'Upgrade your bathroom with luxury fixtures, tile work, walk-in showers, and spa-inspired designs.'
  },
  {
    icon: 'ri-layout-bottom-2-line',
    title: 'Basement Finishing',
    description: 'Convert your unfinished basement into a functional living space — home office, gym, entertainment room or suite.'
  },
  {
    icon: 'ri-add-box-line',
    title: 'Room Additions',
    description: 'Expand your living space with seamlessly integrated room additions that match your home\'s existing architecture.'
  },
  {
    icon: 'ri-paint-brush-line',
    title: 'Interior Remodeling',
    description: 'Full interior renovations including flooring, walls, ceilings, trim, and all finishing touches.'
  },
  {
    icon: 'ri-building-2-line',
    title: 'Whole Home Renovation',
    description: 'Complete top-to-bottom home transformations, planned and executed with precision from start to finish.'
  }
];

const benefits = [
  {
    icon: 'ri-pencil-ruler-2-line',
    title: 'Custom Design',
    description: 'Every remodeling project is tailored to your vision, lifestyle, and budget for a truly personalized result.'
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Licensed & Insured',
    description: 'Fully licensed, bonded, and insured — your investment is protected at every stage of the project.'
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'On Schedule',
    description: 'We respect your time and deliver projects on schedule with transparent communication throughout.'
  },
  {
    icon: 'ri-award-line',
    title: 'Quality Craftsmanship',
    description: '12+ years of experience delivering superior workmanship in every detail, large or small.'
  }
];

export default function RemodelingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleContactClick = () => {
    navigate('/');
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 400);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://public.readdy.ai/ai/img_res/ad48031e255c0f7a14cea7d5742e50d4.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60"></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <div className="mb-6">
              <span className="text-brand text-sm font-semibold uppercase tracking-wider">
                Home Transformation
              </span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Remodeling Services
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              From kitchens and bathrooms to full home renovations, we bring your vision to life with expert craftsmanship and attention to every detail.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={handleContactClick}
                className="px-8 py-4 bg-brand text-white font-semibold rounded-lg hover:bg-brand-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                Request Free Estimate
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap cursor-pointer"
              >
                View All Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Remodeling Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="mb-4">
              <span className="text-brand text-sm font-semibold uppercase tracking-wider">
                What We Remodel
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Every Space, Transformed
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We remodel every part of your home — from a single room to a complete whole-home renovation, always with quality you can see.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {remodelingTypes.map((type, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-2xl p-8 hover:bg-brand/5 transition-all duration-300 hover:-translate-y-1 cursor-default"
              >
                <div className="w-14 h-14 flex items-center justify-center mb-6">
                  <i className={`${type.icon} text-4xl text-brand`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {type.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {type.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="mb-4">
              <span className="text-brand text-sm font-semibold uppercase tracking-wider">
                Why Choose Us
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Remodeling Done Right
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6 bg-brand/10 rounded-2xl">
                  <i className={`${benefit.icon} text-3xl text-brand`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* CTA */}
      <section className="py-20 relative overflow-hidden" style={{backgroundImage: 'url(https://readdy.ai/api/search-image?query=beautiful%20modern%20home%20interior%20remodeling%20renovation%20open%20concept%20living%20room%20kitchen%20transformation%20bright%20natural%20light%20new%20flooring%20cabinets%20contemporary%20design%20professional%20photography&width=1600&height=700&seq=ctaremodeling1&orientation=landscape)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
        <div className="absolute inset-0 bg-black/55"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Home?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Let's talk about your remodeling goals and design a plan that brings your dream home to life.
          </p>
          <button
            onClick={handleContactClick}
            className="px-10 py-4 bg-white text-brand font-bold rounded-lg hover:bg-gray-100 transition-colors text-lg whitespace-nowrap cursor-pointer"
          >
            Get Free Estimate
          </button>
        </div>
      </section>

      <Footer />


    </div>
  );
}
